import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mockJobs } from "@/mocks/jobs";
import SEO from "@/components/base/SEO";
import JobDetailDrawer from "./components/JobDetailDrawer";
import JobMessagingPanel from "./components/JobMessagingPanel";
import VerifiedEmployerModal from "./components/VerifiedEmployerModal";
import PostJobModal from "./components/PostJobModal";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

type JobType = typeof mockJobs[0];

const recruitmentSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Find Your Dream Job in the USA — StarBookPrime Recruitment",
    "description": "Find jobs at verified factories, e-commerce brands, and logistics companies across the USA. Apply, send your resume, and message employers directly. 2,400+ open positions.",
    "url": `${SITE_URL}/recruitment`,
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Recruitment", "item": `${SITE_URL}/recruitment` }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": "Factory & Warehouse Jobs in the USA",
    "description": "Find jobs at verified factories, e-commerce brands, and logistics companies across the USA. 2,400+ open positions. Free to apply for all USA citizens.",
    "hiringOrganization": {
      "@type": "Organization",
      "name": "StarBookPrime",
      "sameAs": SITE_URL,
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "US",
      },
    },
    "employmentType": ["FULL_TIME", "PART_TIME", "CONTRACTOR"],
    "datePosted": "2026-04-13",
    "validThrough": "2026-12-31",
    "applicantLocationRequirements": {
      "@type": "Country",
      "name": "United States",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "EmployerAggregateRating",
    "itemReviewed": {
      "@type": "Organization",
      "name": "StarBookPrime Recruitment",
    },
    "ratingValue": "4.8",
    "bestRating": "5",
    "ratingCount": "2400",
  }
];

const categoryIcons: Record<string, string> = {
  Manufacturing: "ri-settings-3-line",
  Sales: "ri-store-2-line",
  Marketing: "ri-megaphone-line",
  Logistics: "ri-truck-line",
  Creative: "ri-palette-line",
  "Customer Service": "ri-headphone-line",
  All: "ri-apps-line",
};

export default function RecruitmentPage() {
  const [jobs, setJobs] = useState<JobType[]>(mockJobs);
  const [typeFilter, setTypeFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState<JobType | null>(null);
  const [showMessaging, setShowMessaging] = useState(false);
  const [messagingRole, setMessagingRole] = useState<"boss" | "employee">("employee");
  const [viewAs, setViewAs] = useState<"boss" | "employee">("employee");
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showPostJob, setShowPostJob] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  const types = ["All", "Full-time", "Part-time", "Contract"];
  const categories = ["All", "Manufacturing", "Sales", "Marketing", "Logistics", "Creative", "Customer Service"];

  const filtered = jobs.filter((j) => {
    const matchType = typeFilter === "All" || j.type === typeFilter;
    const matchCat = categoryFilter === "All" || j.category === categoryFilter;
    const matchSearch = !search || j.title.toLowerCase().includes(search.toLowerCase()) || j.company.toLowerCase().includes(search.toLowerCase()) || j.location.toLowerCase().includes(search.toLowerCase());
    return matchType && matchCat && matchSearch;
  });

  const handleUpdate = (updated: JobType) => {
    setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
    setSelectedJob(updated);
  };

  const openMessaging = (role: "boss" | "employee") => {
    setMessagingRole(role);
    setShowMessaging(true);
  };

  const handlePostJobClick = () => {
    if (!isVerified) {
      setShowVerifyModal(true);
    } else {
      setShowPostJob(true);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Find Your Dream Job in the USA | StarBookPrime Recruitment"
        description="Find jobs at verified factories, e-commerce brands, and logistics companies across the USA. Apply, send your resume, and message employers directly."
        keywords="USA factory jobs, local manufacturing jobs, e-commerce warehouse jobs"
        canonical="/recruitment"
        schema={recruitmentSchema}
      />
      <Navbar />

      {/* Modals */}
      {selectedJob && (
        <JobDetailDrawer
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdate={handleUpdate}
          viewAs={viewAs}
        />
      )}
      {showMessaging && (
        <JobMessagingPanel
          onClose={() => setShowMessaging(false)}
          viewAs={messagingRole}
        />
      )}
      {showVerifyModal && (
        <VerifiedEmployerModal
          onClose={() => setShowVerifyModal(false)}
          onVerified={() => { setIsVerified(true); setShowVerifyModal(false); setShowPostJob(true); }}
        />
      )}
      {showPostJob && (
        <PostJobModal
          onClose={() => setShowPostJob(false)}
          onPosted={(newJob) => {
            setJobs((prev) => [newJob, ...prev]);
            setShowPostJob(false);
          }}
        />
      )}

      <div className="pt-16">
        {/* Hero Banner */}
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d1a00 50%, #4a2800 100%)" }}>
          <img
            src="https://public.readdy.ai/ai/img_res/78a33b0fe7341f1c6f626ca1f221b235.jpg"
            alt="USA Jobs"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-25"
          />
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">Now Hiring</span>
                  <span className="bg-green-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1"><i className="ri-shield-check-fill" /> Verified Companies Only</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-3">
                  Find Your Dream Job<br />
                  <span className="text-orange-400">Right Here in the USA</span>
                </h1>
                <p className="text-gray-300 text-base max-w-lg mb-4">
                  Every job is posted by a <strong className="text-white">verified factory or company</strong>. Apply, send your resume, and message employers — all 100% free for USA citizens.
                </p>
                {/* Free apply badge */}
                <div className="flex flex-wrap gap-2">
                  <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-400/40 text-green-300 text-xs font-bold px-4 py-2 rounded-full">
                    <i className="ri-gift-line" /> Apply &amp; Send Resume — Always FREE
                  </div>
                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-gray-300 text-xs font-bold px-4 py-2 rounded-full">
                    <i className="ri-shield-check-fill text-orange-400" /> All Jobs Verified &amp; Real
                  </div>
                </div>
              </div>

              {/* View Toggle */}
              <div className="flex-shrink-0">
                <p className="text-gray-400 text-xs font-semibold mb-2 text-center">I am a...</p>
                <div className="flex gap-2 p-1 bg-white/10 rounded-2xl">
                  <button
                    onClick={() => setViewAs("employee")}
                    className={`px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${viewAs === "employee" ? "bg-orange-500 text-white" : "text-gray-300 hover:text-white"}`}
                  >
                    <i className="ri-user-line" /> Job Seeker
                  </button>
                  <button
                    onClick={() => setViewAs("boss")}
                    className={`px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${viewAs === "boss" ? "bg-orange-500 text-white" : "text-gray-300 hover:text-white"}`}
                  >
                    <i className="ri-building-2-line" /> Employer
                  </button>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => openMessaging("employee")}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-xl hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-message-3-line" /> Applicant Inbox
                  </button>
                  <button
                    onClick={() => openMessaging("boss")}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-xl hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-line" /> Employer Inbox
                  </button>
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {[
                { icon: "ri-briefcase-4-line", value: "2,400+", label: "Open Positions" },
                { icon: "ri-shield-check-fill", value: "180+", label: "Verified Companies" },
                { icon: "ri-map-pin-2-line", value: "48 States", label: "Nationwide" },
                { icon: "ri-gift-line", value: "100% Free", label: "To Apply & Send Resume" },
              ].map((s) => (
                <div key={s.label} className="bg-white/10 border border-white/15 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 rounded-lg flex-shrink-0">
                    <i className={`${s.icon} text-orange-400 text-lg`} />
                  </div>
                  <div>
                    <p className="text-white font-extrabold text-lg leading-none">{s.value}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How It Works — Trust Strip */}
        <div className="bg-gray-50 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-0 justify-between">
              {[
                { icon: "ri-shield-check-fill", color: "text-green-500", title: "Verified Employers Only", desc: "Every company is verified with EIN & business license before posting" },
                { icon: "ri-gift-line", color: "text-orange-500", title: "Free for Job Seekers", desc: "Apply, send resume, and message employers — always 100% free" },
                { icon: "ri-message-3-line", color: "text-orange-500", title: "Chat Directly with the Boss", desc: "No recruiters, no middlemen — talk straight to the hiring manager" },
                { icon: "ri-user-star-line", color: "text-orange-500", title: "Built for Young USA Citizens", desc: "Entry-level friendly jobs from real factories across all 48 states" },
              ].map((item, i) => (
                <div key={item.title} className="flex items-start gap-3 flex-1 px-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-gray-100 flex-shrink-0">
                    <i className={`${item.icon} ${item.color} text-lg`} />
                  </div>
                  <div>
                    <p className="text-sm font-extrabold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{item.desc}</p>
                  </div>
                  {i < 3 && <div className="hidden md:block w-px h-10 bg-gray-200 ml-4 flex-shrink-0 self-center" />}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="bg-white border-b border-gray-100 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
            {/* Search Bar */}
            <div className="relative mb-4">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400 transition-colors"
                placeholder="Search by job title, company, or city..."
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              )}
            </div>

            {/* Filter Rows */}
            <div className="flex flex-wrap gap-2 mb-2">
              {types.map((t) => (
                <button key={t} onClick={() => setTypeFilter(t)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${typeFilter === t ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>{t}</button>
              ))}
              <span className="w-px h-6 bg-gray-200 self-center mx-1" />
              {categories.map((c) => (
                <button key={c} onClick={() => setCategoryFilter(c)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${categoryFilter === c ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600"}`}>
                  <i className={categoryIcons[c] || "ri-briefcase-line"} />{c}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400">{filtered.length} job{filtered.length !== 1 ? "s" : ""} found · All from verified employers</p>
          </div>
        </div>

        {/* Job Listings */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">

          {/* Employer Mode Banner */}
          {viewAs === "boss" && (
            <div className="mb-8">
              {isVerified ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                      <i className="ri-shield-check-fill text-green-500 text-lg" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-green-800"><i className="ri-shield-check-fill mr-1" />Verified Employer — Active</p>
                      <p className="text-xs text-green-600">You can post unlimited jobs. All listings show your verified badge.</p>
                    </div>
                  </div>
                  <button
                    onClick={handlePostJobClick}
                    className="flex items-center gap-2 px-6 py-2.5 bg-green-500 text-white text-xs font-bold rounded-full hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                  >
                    <i className="ri-add-circle-line" /> Post a New Job
                  </button>
                </div>
              ) : (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <p className="text-sm font-extrabold text-orange-800 mb-1"><i className="ri-building-2-line mr-1" />Want to Post Jobs &amp; Hire Young USA Talent?</p>
                      <p className="text-xs text-orange-600">Get verified first — it only takes a few minutes and costs just $5/month.</p>
                    </div>
                    <button
                      onClick={handlePostJobClick}
                      className="flex items-center gap-2 px-6 py-2.5 bg-orange-500 text-white text-xs font-bold rounded-full hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                    >
                      <i className="ri-shield-check-line" /> Get Verified &amp; Post Jobs
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {[
                      { icon: "ri-shield-check-fill", text: "Verified badge on all your listings", color: "text-green-500" },
                      { icon: "ri-briefcase-4-line", text: "Post unlimited job openings", color: "text-orange-500" },
                      { icon: "ri-gift-line", text: "Job seekers apply FREE — you only pay $5/mo", color: "text-orange-500" },
                    ].map((item) => (
                      <div key={item.text} className="flex items-center gap-2 bg-white rounded-xl p-3 border border-orange-100">
                        <i className={`${item.icon} ${item.color} flex-shrink-0`} />
                        <span className="text-xs text-gray-700 font-medium">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Job Seeker Free Apply Banner */}
          {viewAs === "employee" && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-8 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                <i className="ri-gift-line text-green-500 text-lg" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-extrabold text-green-800">Applying is 100% FREE for all USA Citizens</p>
                <p className="text-xs text-green-600 mt-0.5">Send your resume, message employers, and apply to any job — no fees, no subscriptions, ever.</p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-xs text-green-700 font-bold bg-green-100 px-3 py-1.5 rounded-full whitespace-nowrap">
                <i className="ri-shield-check-fill" /> All Jobs Verified
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((job) => (
              <div
                key={job.id}
                onClick={() => setSelectedJob(job)}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-orange-200 hover:-translate-y-0.5 transition-all cursor-pointer group"
              >
                {/* Cover */}
                <div className="relative h-36 overflow-hidden">
                  <img src={job.coverImage} alt={job.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {job.urgent && (
                    <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">Urgent</span>
                  )}
                  {/* Verified badge */}
                  <span className={`absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${job.verified ? "bg-green-500 text-white" : "bg-gray-500 text-white"}`}>
                    <i className={job.verified ? "ri-shield-check-fill" : "ri-time-line"} />
                    {job.verified ? "Verified" : "Pending"}
                  </span>
                  <div className="absolute bottom-3 right-3 bg-white/90 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className={`${categoryIcons[job.category] || "ri-briefcase-line"} text-orange-500`} />{job.category}
                  </div>
                </div>

                <div className="p-5">
                  {/* Header */}
                  <div className="flex gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0">
                      <img src={job.logo} alt={job.company} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-extrabold text-gray-900 text-sm leading-snug truncate">{job.title}</h3>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-xs text-gray-500">{job.company}</span>
                        {job.verified && <i className="ri-shield-check-fill text-green-500 text-xs" title="Verified Employer" />}
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{job.description}</p>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    <span className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full"><i className="ri-map-pin-line" />{job.location}</span>
                    <span className="flex items-center gap-1 text-xs bg-orange-50 text-orange-600 px-2.5 py-1 rounded-full font-semibold"><i className="ri-money-dollar-circle-line" />{job.salary}</span>
                    <span className="flex items-center gap-1 text-xs bg-sky-50 text-sky-600 px-2.5 py-1 rounded-full"><i className="ri-time-line" />{job.type}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-user-line" />{job.applicants} applied</span>
                      <span>{job.posted}</span>
                    </div>
                    {viewAs === "employee" ? (
                      <span className="px-4 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-full group-hover:bg-orange-600 transition-colors whitespace-nowrap">
                        Apply Free
                      </span>
                    ) : (
                      <span className="px-4 py-1.5 border border-orange-300 text-orange-600 text-xs font-bold rounded-full group-hover:bg-orange-50 transition-colors whitespace-nowrap">
                        <i className="ri-edit-line mr-1" />Edit
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                <i className="ri-search-line text-gray-400 text-2xl" />
              </div>
              <p className="text-gray-500 font-semibold">No jobs found</p>
              <p className="text-gray-400 text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-14 relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=young%20diverse%20professionals%20celebrating%20success%20USA%20office%20team%20high%20five%20smiling%20bright%20modern%20workspace%20energy%20vibrant&width=1400&height=280&seq=rec-bottom-cta&orientation=landscape"
              alt="Join the team"
              className="w-full h-56 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-transparent" />
            <div className="absolute inset-0 flex items-center px-8 md:px-14">
              <div className="max-w-lg">
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-2">Are You a Factory or Brand?</h3>
                <p className="text-gray-300 text-sm mb-2">Get verified and post jobs to reach motivated young USA talent. Only <strong className="text-orange-400">$5/month</strong> — job seekers always apply free.</p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handlePostJobClick}
                    className="inline-flex items-center gap-2 bg-orange-500 text-white px-7 py-3 rounded-full font-bold text-sm hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shield-check-line" /> Get Verified &amp; Post Jobs — $5/mo
                  </button>
                  <button
                    onClick={() => openMessaging("boss")}
                    className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white px-7 py-3 rounded-full font-bold text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-message-3-line" /> View Applicants
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
